import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { testAPI, analyticsAPI } from '../../services/api';
import toast from 'react-hot-toast';
import { 
  Play, 
  Clock, 
  BookOpen, 
  Award, 
  Users, 
  TrendingUp,
  Target,
  CheckCircle,
  Lock
} from 'lucide-react';

interface Test {
  id: number;
  title: string;
  description: string;
  duration: number;
  totalQuestions: number;
  subjects: string[];
  difficulty: string;
  totalMarks: number;
  isActive: boolean;
  createdAt: string;
}

interface UsageInfo {
  isPremium: boolean;
  testsUsed: number;
  testsLimit: number;
  testsRemaining: number | null;
}

export default function TestListPage() {
  const navigate = useNavigate();
  const [tests, setTests] = useState<Test[]>([]);
  const [loading, setLoading] = useState(true);
  const [startingTest, setStartingTest] = useState<number | null>(null);
  const [dashStats, setDashStats] = useState({ testsCompleted: 0, averageScore: 0, rank: 0 });
  const [testSummaries, setTestSummaries] = useState<Record<number, { attempts: number; bestScore: number }>>({});
  const [usage, setUsage] = useState<UsageInfo | null>(null);

  useEffect(() => {
    fetchTests();
    analyticsAPI.getDashboardStats()
      .then(r => setDashStats(r.data.data))
      .catch(() => {});
    analyticsAPI.getTestSummaries()
      .then(r => setTestSummaries(r.data.data))
      .catch(() => {});
    testAPI.getUsage()
      .then(r => setUsage(r.data.data))
      .catch(() => {});
  }, []);

  const fetchTests = async () => {
    try {
      const response = await testAPI.getTests();
      if (response.data.success) {
        setTests(response.data.data);
      }
    } catch (error: any) {
      toast.error('Failed to load tests');
      console.error('Fetch tests error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStartTest = async (testId: number) => {
    setStartingTest(testId);
    try {
      const response = await testAPI.startTest(testId.toString());
      if (response.data.success) {
        const { attemptId, isResuming } = response.data.data;
        toast.success(isResuming ? 'Resuming test...' : 'Starting test...');
        // Refresh usage count after starting a new test
        testAPI.getUsage().then(r => setUsage(r.data.data)).catch(() => {});
        navigate(`/test/${testId}?attempt=${attemptId}`);
      }
    } catch (error: any) {
      const errCode = error.response?.data?.code;
      if (errCode === 'FREE_PLAN_LIMIT_REACHED') {
        toast.error('Monthly test limit reached. Upgrade to Pro for unlimited tests.');
        // Refresh usage to reflect accurate count
        testAPI.getUsage().then(r => setUsage(r.data.data)).catch(() => {});
      } else {
        toast.error(error.response?.data?.error || 'Failed to start test');
      }
    } finally {
      setStartingTest(null);
    }
  };

  const isLimitReached = usage !== null && !usage.isPremium && (usage.testsRemaining ?? 1) <= 0;

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty.toLowerCase()) {
      case 'easy': return 'success';
      case 'medium': return 'warning';
      case 'hard': return 'danger';
      default: return 'secondary';
    }
  };

  const getSubjectColor = (subject: string) => {
    switch (subject.toLowerCase()) {
      case 'mathematics': return 'primary';
      case 'physics': return 'success';
      case 'chemistry': return 'warning';
      default: return 'secondary';
    }
  };

  if (loading) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <div className="animate-pulse">
          <div className="h-8 bg-secondary-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-32 bg-secondary-200 rounded-xl"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-secondary-900 mb-2">Mock Tests</h1>
        <p className="text-secondary-600">Choose from our comprehensive collection of JEE practice tests</p>
      </div>

      {/* Free plan usage banner */}
      {usage && !usage.isPremium && (
        <div className={`mb-6 rounded-xl p-4 flex items-center justify-between ${
          isLimitReached
            ? 'bg-red-50 border border-red-200'
            : 'bg-amber-50 border border-amber-200'
        }`}>
          <div className="flex items-center gap-3">
            {isLimitReached
              ? <Lock className="h-5 w-5 text-red-500 shrink-0" />
              : <BookOpen className="h-5 w-5 text-amber-500 shrink-0" />
            }
            <div>
              <p className={`font-medium text-sm ${isLimitReached ? 'text-red-800' : 'text-amber-800'}`}>
                {isLimitReached
                  ? 'Monthly test limit reached'
                  : `${usage.testsRemaining} of ${usage.testsLimit} free tests remaining this month`
                }
              </p>
              <p className={`text-xs mt-0.5 ${isLimitReached ? 'text-red-600' : 'text-amber-600'}`}>
                {isLimitReached
                  ? 'Upgrade to Pro for unlimited mock tests'
                  : 'Free plan: 4 tests per month. Upgrade for unlimited access.'
                }
              </p>
            </div>
          </div>
          <a
            href="/subscription"
            className="shrink-0 text-sm font-medium px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
          >
            Upgrade
          </a>
        </div>
      )}

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="stats-card">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-gradient-to-r from-primary-500 to-primary-600 rounded-xl flex items-center justify-center">
              <BookOpen className="h-6 w-6 text-white" />
            </div>
          </div>
          <div className="stats-value">{tests.length}</div>
          <div className="stats-label">Available Tests</div>
        </div>

        <div className="stats-card">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-gradient-to-r from-success-500 to-success-600 rounded-xl flex items-center justify-center">
              <CheckCircle className="h-6 w-6 text-white" />
            </div>
          </div>
          <div className="stats-value">{dashStats.testsCompleted}</div>
          <div className="stats-label">Completed</div>
        </div>

        <div className="stats-card">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-gradient-to-r from-warning-500 to-warning-600 rounded-xl flex items-center justify-center">
              <TrendingUp className="h-6 w-6 text-white" />
            </div>
          </div>
          <div className="stats-value">{dashStats.averageScore > 0 ? `${dashStats.averageScore}%` : '—'}</div>
          <div className="stats-label">Avg Score</div>
        </div>

        <div className="stats-card">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-gradient-to-r from-accent-500 to-accent-600 rounded-xl flex items-center justify-center">
              <Award className="h-6 w-6 text-white" />
            </div>
          </div>
          <div className="stats-value">{dashStats.rank > 0 ? `#${dashStats.rank}` : '—'}</div>
          <div className="stats-label">Rank</div>
        </div>
      </div>

      {/* Test List */}
      <div className="space-y-6">
        {tests.map((test) => (
          <div key={test.id} className="card-interactive group">
            <div className="flex items-start justify-between p-6">
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-3">
                  <h3 className="text-xl font-semibold text-secondary-900 group-hover:text-primary-600 transition-colors">
                    {test.title}
                  </h3>
                  <span className={`badge badge-${getDifficultyColor(test.difficulty)}`}>
                    {test.difficulty}
                  </span>
                </div>
                
                <p className="text-secondary-600 mb-4 leading-relaxed">
                  {test.description}
                </p>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  <div className="flex items-center text-sm text-secondary-600">
                    <Clock className="h-4 w-4 mr-2" />
                    {test.duration} minutes
                  </div>
                  <div className="flex items-center text-sm text-secondary-600">
                    <BookOpen className="h-4 w-4 mr-2" />
                    {test.totalQuestions} questions
                  </div>
                  <div className="flex items-center text-sm text-secondary-600">
                    <Target className="h-4 w-4 mr-2" />
                    {test.totalMarks} marks
                  </div>
                  <div className="flex items-center text-sm text-secondary-600">
                    <Users className="h-4 w-4 mr-2" />
                    {testSummaries[test.id]?.attempts ?? 0} attempts
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <span className="text-sm text-secondary-500">Subjects:</span>
                  {test.subjects.map((subject) => (
                    <span
                      key={subject}
                      className={`badge badge-${getSubjectColor(subject)} badge-sm`}
                    >
                      {subject.charAt(0).toUpperCase() + subject.slice(1)}
                    </span>
                  ))}
                </div>
              </div>

              <div className="flex flex-col items-end space-y-3 ml-6">
                <button
                  onClick={() => handleStartTest(test.id)}
                  disabled={startingTest === test.id || isLimitReached}
                  className={`flex items-center justify-center space-x-2 min-w-[120px] px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                    isLimitReached
                      ? 'bg-secondary-400 border border-secondary-400 cursor-not-allowed'
                      : 'bg-primary-600 border border-primary-600 hover:bg-primary-700'
                  }`}
                >
                  {startingTest === test.id ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      <span>Starting...</span>
                    </>
                  ) : isLimitReached ? (
                    <>
                      <Lock className="h-4 w-4" />
                      <span>Locked</span>
                    </>
                  ) : (
                    <>
                      <Play className="h-4 w-4" />
                      <span>Start Test</span>
                    </>
                  )}
                </button>

                <div className="text-right">
                  <div className="text-sm text-secondary-500">Best Score</div>
                  <div className="text-lg font-semibold text-secondary-900">
                    {testSummaries[test.id]?.bestScore
                      ? `${Math.round(testSummaries[test.id].bestScore)}%`
                      : '—'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {tests.length === 0 && (
        <div className="text-center py-12">
          <div className="w-24 h-24 bg-secondary-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <BookOpen className="h-12 w-12 text-secondary-400" />
          </div>
          <h3 className="text-xl font-semibold text-secondary-900 mb-2">No Tests Available</h3>
          <p className="text-secondary-600">Check back later for new practice tests.</p>
        </div>
      )}
    </div>
  );
}